import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { onboardingHints } from '../../../constants';
import useMedia from '../../../utils/useMedia';
import Dialog from '../../Dialog/Dialog';
import config from 'config';

interface NewReaderFeedbackDialogProps {
  isDocument: boolean;
  isFirstSession: boolean;
  isClosed: boolean;
  onClose: () => void;
  onRemindLater: () => void;
}

const NewReaderFeedbackDialog: React.FC<NewReaderFeedbackDialogProps> = 
({ isDocument, isFirstSession, isClosed, onClose, onRemindLater }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { isMobile } = useMedia();

  const shouldNewReaderFeedbackDialogRender = 
    isDocument 
    && !isFirstSession  // We want to show this dialog on second session
    && !isClosed;  // If the user closed or submitted the feedback dialog, don't show this again

  useEffect(() => {
    if (shouldNewReaderFeedbackDialogRender === true) {
      setIsOpen(true);
    }
  }, [shouldNewReaderFeedbackDialogRender]);
  
  const htmlBody = (
    <Box sx={{ paddingTop: 0 }}>
      <Typography variant='body1'>
        {t('Your opinion helps us to make meinUnterricht even better. Share your feedback!')}
      </Typography>
    </Box>
  );

  const position: 'right-bottom' | 'center' = isMobile ? 'center' : 'right-bottom';
  const dialogProps = {
    id: onboardingHints.newReaderFeedback,
    title: t('Your opinion counts!'),
    onClose: () => {
      setIsOpen(false);
      onClose();
    },
    primaryCta: {
      label: t('Give feedback'),
      onClick: () => {
        window.open(config.newReaderFeedbackModal.url, '_blank');
        setIsOpen(false);
        onClose();
      },
    },
    secondaryCta: {
      label: t('Remind me later'),
      onClick: () => {
        setIsOpen(false);
        onRemindLater();
      },
    },
    htmlBody,
    position,
    dataTestId: 'new-reader-feedback-dialog'
  };

  return (
    <Dialog
      open={isOpen}
      {...dialogProps}
    />
  );
};

export default NewReaderFeedbackDialog;
